import React from 'react'
import { useTranslation } from 'react-i18next'
import { PagingSidePanelType, Box as Section, SidePanel, Tabs } from '@node-space/ui-components'
import { appBannersId } from 'components/app/AppBanners'
import { useComponentHeight } from 'hooks/useComponentHeight'
import { Transaction } from 'types/transaction'
import { TransactionType } from 'types/transaction/TransactionType'
import { highlightRowBasedOnId } from 'utils/table/table'
import { getSupportedPaymentTypes } from 'utils/utils'
import { useTransactionDetails } from '../hooks/useTransactionDetails'
import InternalTransferTransactionActivity from './PaymentTemplates/InternalTransferTransactionActivity'
import { SidePanelBody } from './SidePanelBody'
import { SidePanelHeaderWallet } from './SidePanelHeaderWallet'
import { isTransactionABuySell } from './utils'

interface SidePanelSummaryProps {
  isOpen: boolean
  paging: PagingSidePanelType
  transactionDetails: Transaction
  onClose: () => void
  isTransactionDetailsLoading?: boolean
}
export const SidePanelSummary = ({
  isOpen,
  paging,
  transactionDetails,
  onClose,
  isTransactionDetailsLoading,
}: SidePanelSummaryProps) => {
  const { t } = useTranslation()

  const {
    amountWithCurrency,
    iconUrls,
    transactionType,
    transactionSubType,
    transactionReference,
    currencyFrom,
    currencyTo,
    status,
    hasProofOfPayment,
    externalReference,
    bvnkNetworkBeneficiary,
    bvnkNetworkSender,
  } = useTransactionDetails(transactionDetails)

  const isInternalTransfer = transactionType === TransactionType.INTERNAL_TRANSFER
  const isBvnkNetworkTransfer = transactionType === TransactionType.BVNK_NETWORK_TRANSFER
  const isFiat = transactionDetails?.wallet?.currency?.fiat
  const bannerSpacing = useComponentHeight(appBannersId)

  const supportedPaymentTypes = getSupportedPaymentTypes()

  const isBuySell = isTransactionABuySell(transactionType, transactionSubType)

  const hasTransactionActivities = !!transactionDetails?.context?.transactions?.length
  const showActivityTab =
    (isBvnkNetworkTransfer || (isInternalTransfer && isFiat) || isBuySell) &&
    hasTransactionActivities

  const transactionRowId = `${transactionDetails?.id}-${transactionDetails?.hash}-${transactionDetails?.dateCreated}`

  return (
    <SidePanel
      id="side-panel-summary-transaction"
      isOpen={isOpen}
      paging={paging}
      onClose={onClose}
      activeRowId={transactionRowId}
      onActiveRowChange={highlightRowBasedOnId}
      topOffset={bannerSpacing}
      header={
        <SidePanelHeaderWallet
          data={{
            iconUrls,
            amountWithCurrency,
            transactionReference,
            transactionType,
            transactionSubType,
            currencyFrom,
            currencyTo,
            status,
            hasProofOfPayment,
            externalReference,
            bvnkNetworkBeneficiary,
            bvnkNetworkSender,
          }}
          supportedPaymentTypes={supportedPaymentTypes}
        />
      }
      externalStyle={{ top: `${bannerSpacing}px` }}
      body={
        <Section tag="section" width="full">
          {showActivityTab ? (
            <Tabs
              tabs={[
                {
                  id: 'overview',
                  header: t('overview'),
                  content: (
                    <SidePanelBody
                      transactionDetails={transactionDetails}
                      supportedPaymentTypes={supportedPaymentTypes}
                      isTransactionDetailsLoading={isTransactionDetailsLoading}
                    />
                  ),
                  testId: 'sidepanel-overview-tab',
                },
                {
                  id: 'activity',
                  header: t('transactions.sideBar.activity'),
                  content: (
                    <InternalTransferTransactionActivity
                      transactionActivities={transactionDetails?.context?.transactions ?? []}
                    />
                  ),
                  testId: 'sidepanel-activity-tab',
                },
              ]}
            />
          ) : (
            <SidePanelBody
              transactionDetails={transactionDetails}
              supportedPaymentTypes={supportedPaymentTypes}
              isTransactionDetailsLoading={isTransactionDetailsLoading}
            />
          )}
        </Section>
      }
    />
  )
}
